// src/components/admin/AdminNavBuilder.jsx

import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
    Eye, EyeOff, GripVertical, Save, RefreshCw,
    Home, Heart, Brain, Moon, Mic, Users, Star, BrainCircuit,
    Briefcase, ArrowRightCircle, HeartHandshake, Compass, Pencil, Plus, Trash2
} from 'lucide-react';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase-config';
import { NAV_TOPIC_GROUPS } from '../../data/navTopics';
import { CUSTOM_NAV_ICONS, makeCustomId, parsePairs, serializePairs } from '../../utils/customNav';

// Blank landing-page form. List fields are textareas of "a | b" lines.
const EMPTY_PAGE = { eyebrow: '', headline: '', tagline: '', intro: '', statement: '', stepsTitle: '', commitments: '', steps: '', actions: '' };

// --- 1. THE MASTER DEFINITION ---
const MASTER_SIDEBAR_ITEMS = [
    { id: 'dashboard', label: 'My Hub', icon: 'Home' },
    { id: 'mental-health', label: 'Mental Health', icon: 'Heart' },
    { id: 'mental-performance', label: 'Mental Performance', icon: 'Brain' },
    { id: 'sleep', label: 'Sleep Optimization', icon: 'Moon' },
    { id: 'performance-123', label: 'Performance 1-2-3', icon: 'Mic' },
    { id: 'family-huddle', label: 'Family Huddle', icon: 'Users' },
    { id: 'kidzcorner', label: 'Kidz Corner', icon: 'Star' },
    { id: 'leadership', label: 'Leadership Hub', icon: 'Briefcase' },
    { id: 'transition', label: 'Transition', icon: 'Compass' }, 
    { id: 'career', label: 'Career Growth', icon: 'ArrowRightCircle' },
    { id: 'brain-health', label: 'Brain Health', icon: 'BrainCircuit' },
    { id: 'reset-recharge', label: 'Reset & Recharge', icon: 'HeartHandshake' },
];

// Helper to render the icons in the builder UI
const IconMap = {
    Home, Heart, Brain, Moon, Mic, Users, Star, BrainCircuit,
    Briefcase, ArrowRightCircle, HeartHandshake, Compass
};

const AdminNavBuilder = ({ organizationId }) => {
    const [items, setItems] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    // ORG-DEFINED SECTIONS (utils/customNav.js). Entries without an `id` are new
    // and get one at save time, generated once from the label and never again.
    const [customNav, setCustomNav] = useState([]);
    const [customNavLabel, setCustomNavLabel] = useState('');

    // --- 2. LOAD CONFIG ---
    useEffect(() => {
        const loadConfig = async () => {
            // Default to Master List (all visible)
            const defaultItems = MASTER_SIDEBAR_ITEMS.map(i => ({ ...i, isVisible: true }));
            setCustomNav([]);
            setCustomNavLabel('');

            if (!organizationId) {
                setItems(defaultItems);
                return;
            }

            try {
                const docRef = doc(db, "organizations", organizationId);
                const snap = await getDoc(docRef);

                if (snap.exists()) {
                    const data = snap.data();
                    setCustomNavLabel(data.customNavLabel || '');
                    const sections = (Array.isArray(data.customNav) ? data.customNav : []).map(s => ({
                        id: s.id, label: s.label || '', icon: s.icon || CUSTOM_NAV_ICONS[0],
                        kind: s.kind === 'landing' ? 'landing' : 'section',
                        description: s.description || '',
                        page: { ...EMPTY_PAGE },
                        subItems: (Array.isArray(s.subItems) ? s.subItems : []).map(t => ({ id: t.id, label: t.label || '', description: t.description || '' }))
                    }));
                    // A landing page's content lives on its private_topics doc, not on
                    // the org doc. Load it into the textarea form.
                    await Promise.all(sections.filter(s => s.kind === 'landing' && s.id).map(async (s) => {
                        const pSnap = await getDoc(doc(db, "organizations", organizationId, "private_topics", s.id));
                        const l = (pSnap.exists() && pSnap.data().landing) || {};
                        s.page = {
                            eyebrow: l.eyebrow || '', headline: l.headline || '', tagline: l.tagline || '',
                            intro: l.intro || '', statement: l.statement || '', stepsTitle: l.stepsTitle || '',
                            commitments: serializePairs(l.commitments, 'keyword', 'text'),
                            steps: serializePairs(l.steps, 'title', 'text'),
                            actions: serializePairs(l.actions, 'label', 'target')
                        };
                    }));
                    setCustomNav(sections);
                }

                if (snap.exists() && snap.data().navConfig) {
                    const savedConfig = snap.data().navConfig;
                    
                    // MERGE LOGIC: 
                    // 1. Use saved order/visibility/LABEL.
                    // 2. If Master has new items not in saved, add them to the end.
                    const mergedItems = [];
                    const savedIds = new Set(savedConfig.map(i => i.id));

                    // Add saved items (preserving order AND SAVED LABELS)
                    savedConfig.forEach(savedItem => {
                        const masterDef = MASTER_SIDEBAR_ITEMS.find(m => m.id === savedItem.id);
                        if (masterDef) {
                            mergedItems.push({
                                ...masterDef, // Keep master icon definition
                                label: savedItem.label || masterDef.label, // Use saved label if exists, else master
                                isVisible: savedItem.isVisible, // Use saved visibility
                                // Sub-topic allowlist; undefined = every sub-topic is on.
                                subItems: Array.isArray(savedItem.subItems) ? savedItem.subItems : undefined
                            });
                        }
                    });

                    // Append any new items from Master that weren't in Saved
                    MASTER_SIDEBAR_ITEMS.forEach(masterItem => {
                        if (!savedIds.has(masterItem.id)) {
                            mergedItems.push({ ...masterItem, isVisible: true });
                        }
                    });

                    setItems(mergedItems);
                } else {
                    setItems(defaultItems);
                }
            } catch (error) {
                console.error("Config Load Error", error);
                setItems(defaultItems);
            }
        };
        loadConfig();
    }, [organizationId]);

    // --- 3. HANDLERS ---
    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        const newItems = Array.from(items);
        const [reorderedItem] = newItems.splice(result.source.index, 1);
        newItems.splice(result.destination.index, 0, reorderedItem);
        setItems(newItems);
    };

    const toggleVisibility = (index) => {
        const newItems = [...items];
        newItems[index].isVisible = !newItems[index].isVisible;
        setItems(newItems);
    };

    // --- SUB-TOPIC ALLOWLIST ---
    // `subItems` undefined means "all on". The first toggle materialises the full
    // list so there is something to remove from.
    const toggleSubItem = (index, subId) => {
        const newItems = [...items];
        const all = (NAV_TOPIC_GROUPS[newItems[index].id] || []).map(t => t.id);
        const current = Array.isArray(newItems[index].subItems) ? newItems[index].subItems : all;
        const next = current.includes(subId) ? current.filter(id => id !== subId) : [...current, subId];
        // Keep platform order, so the saved list is stable however it was clicked.
        newItems[index] = { ...newItems[index], subItems: all.filter(id => next.includes(id)) };
        setItems(newItems);
    };

    // --- NEW: HANDLE LABEL CHANGE ---
    const handleLabelChange = (index, newLabel) => {
        const newItems = [...items];
        newItems[index].label = newLabel;
        setItems(newItems);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // We save ID, Label, and isVisible.
            // subItems is written only when it NARROWS the section. A full list is
            // left off so newly added platform topics appear by default, and
            // Firestore never receives an undefined field.
            const configToSave = items.map(({ id, label, isVisible, subItems }) => {
                const all = (NAV_TOPIC_GROUPS[id] || []).map(t => t.id);
                const narrowed = Array.isArray(subItems) && subItems.length < all.length;
                return narrowed ? { id, label, isVisible, subItems } : { id, label, isVisible };
            });
            
            // CUSTOM SECTIONS. Blank rows are dropped. New rows get their id here,
            // once: ids are URL hashes and Firestore doc ids, so an existing id is
            // never regenerated when its label changes.
            const taken = customNav.flatMap(s => [s.id, ...s.subItems.map(t => t.id)]).filter(Boolean);
            const drafts = customNav
                .map(s => ({ ...s, label: (s.label || '').trim(), subItems: s.subItems.filter(t => (t.label || '').trim()) }))
                // A plain section needs at least one topic; a landing page is itself the page.
                .filter(s => s.label && (s.kind === 'landing' || s.subItems.length > 0))
                .map(s => {
                    const id = s.id || makeCustomId(s.label, taken);
                    if (!s.id) taken.push(id);
                    return { ...s, id };
                });

            // What goes on the ORG doc: structure only. Landing content stays on the
            // page's own private_topics doc, so the org doc does not grow with copy.
            const sectionsToSave = drafts.map(s => ({
                id: s.id, label: s.label, icon: s.icon || CUSTOM_NAV_ICONS[0],
                kind: s.kind === 'landing' ? 'landing' : 'section',
                description: (s.description || '').trim(),
                subItems: s.kind === 'landing' ? [] : s.subItems.map(t => {
                    const tid = t.id || makeCustomId(t.label, taken);
                    if (!t.id) taken.push(tid);
                    return { id: tid, label: t.label.trim(), description: (t.description || '').trim() };
                })
            }));

            await updateDoc(doc(db, "organizations", organizationId), {
                navConfig: configToSave,
                customNav: sectionsToSave,
                customNavLabel: (customNavLabel || '').trim()
            });

            // Seed each topic's page. TopicView renders from the org's
            // private_topics doc and shows "Topic Not Found" without a title or
            // description. merge:true, so content already loaded through the
            // Content Library (modules) is never touched.
            await Promise.all(sectionsToSave.flatMap(s => s.subItems.map(t =>
                setDoc(doc(db, "organizations", organizationId, "private_topics", t.id), {
                    title: t.label,
                    description: t.description || `${t.label} resources from your organization.`
                }, { merge: true })
            )));

            // Landing pages: write the page content. The first action is the primary
            // button. merge:true keeps any resources loaded through Content Library.
            await Promise.all(drafts.filter(s => s.kind === 'landing').map(s => {
                const pg = s.page || EMPTY_PAGE;
                return setDoc(doc(db, "organizations", organizationId, "private_topics", s.id), {
                    title: s.label,
                    description: (s.description || '').trim() || (pg.intro || '').trim() || `${s.label}.`,
                    landing: {
                        eyebrow: (pg.eyebrow || '').trim(), headline: (pg.headline || '').trim(),
                        tagline: (pg.tagline || '').trim(), intro: (pg.intro || '').trim(),
                        statement: (pg.statement || '').trim(), stepsTitle: (pg.stepsTitle || '').trim(),
                        commitments: parsePairs(pg.commitments, 'keyword', 'text'),
                        steps: parsePairs(pg.steps, 'title', 'text'),
                        actions: parsePairs(pg.actions, 'label', 'target').map((a, i) => ({ ...a, primary: i === 0 }))
                    }
                }, { merge: true });
            }));

            // Keep each draft's page form; refresh ids so a second save updates in place.
            setCustomNav(sectionsToSave.map(s => ({ ...s, page: (drafts.find(d => d.id === s.id) || {}).page || { ...EMPTY_PAGE } })));
            alert("Organization menu updated successfully!");
        } catch (error) {
            console.error("Error saving nav:", error);
            alert("Failed to save configuration.");
        }
        setIsSaving(false);
    };

    const handleReset = () => {
        if(window.confirm("Reset to default layout? This will lose all custom names.")) {
            setItems(MASTER_SIDEBAR_ITEMS.map(i => ({ ...i, isVisible: true })));
        }
    };

    return (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm max-w-2xl">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">Menu Configuration</h2>
                    <p className="text-xs text-slate-500">Customize labels, visibility, and which topics each section offers. Hidden content is unreachable, not just unlisted.</p>
                </div>
                <div className="flex gap-2">
                    <button 
                         onClick={handleReset}
                         className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                         title="Reset to Default"
                    >
                        <RefreshCw size={18} />
                    </button>
                    <button 
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-slate-800 disabled:opacity-50"
                    >
                        <Save size={16} /> {isSaving ? "Saving..." : "Save Config"}
                    </button>
                </div>
            </div>

            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="nav-builder">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                            {items.map((item, index) => {
                                const IconComponent = IconMap[item.icon] || Home;
                                return (
                                    <Draggable key={item.id} draggableId={item.id} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={`p-3 rounded-xl border transition-colors ${
                                                    snapshot.isDragging
                                                        ? 'shadow-lg border-teal-500 bg-teal-50'
                                                        : item.isVisible ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'
                                                }`}
                                            >
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4 flex-1">
                                                    <div {...provided.dragHandleProps} className="text-slate-400 cursor-grab hover:text-slate-600 active:cursor-grabbing">
                                                        <GripVertical size={20} />
                                                    </div>
                                                    
                                                    <div className={`p-2 rounded-lg ${item.isVisible ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-300'}`}>
                                                        <IconComponent size={20} />
                                                    </div>
                                                    
                                                    <div className="flex-1">
                                                        {/* EDITABLE INPUT FIELD */}
                                                        <div className="flex items-center gap-2">
                                                            <input 
                                                                type="text"
                                                                className={`font-bold text-sm bg-transparent border-b border-transparent hover:border-slate-300 focus:border-brand-primary outline-none transition-colors w-full ${item.isVisible ? 'text-slate-800' : 'text-slate-400 line-through decoration-slate-300'}`}
                                                                value={item.label}
                                                                onChange={(e) => handleLabelChange(index, e.target.value)}
                                                                disabled={!item.isVisible}
                                                            />
                                                            <Pencil size={12} className="text-slate-300 opacity-0 group-hover:opacity-100" />
                                                        </div>
                                                        <p className="text-[10px] text-slate-400 font-mono">{item.id}</p>
                                                    </div>
                                                </div>

                                                <button 
                                                    onClick={() => toggleVisibility(index)}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ml-4 ${
                                                        item.isVisible 
                                                            ? 'bg-teal-50 text-teal-700 border border-teal-200' 
                                                            : 'bg-slate-100 text-slate-400 border border-slate-200'
                                                    }`}
                                                >
                                                    {item.isVisible ? (
                                                        <> <Eye size={14} /> Visible </>
                                                    ) : (
                                                        <> <EyeOff size={14} /> Hidden </>
                                                    )}
                                                </button>
                                              </div>

                                              {/* SUB-TOPICS: which topics inside this section the org offers.
                                                  Off means unreachable, not just unlisted; the router enforces
                                                  the same list (utils/navAccess.js). */}
                                              {item.isVisible && NAV_TOPIC_GROUPS[item.id] && (
                                                  <div className="mt-3 pt-3 border-t border-slate-100 pl-[3.25rem]">
                                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Topics offered</p>
                                                      <div className="flex flex-wrap gap-1.5">
                                                          {NAV_TOPIC_GROUPS[item.id].map(topic => {
                                                              const on = !Array.isArray(item.subItems) || item.subItems.includes(topic.id);
                                                              return (
                                                                  <button
                                                                      key={topic.id}
                                                                      type="button"
                                                                      onClick={() => toggleSubItem(index, topic.id)}
                                                                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                                                                          on ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-white text-slate-400 border-slate-200 line-through'
                                                                      }`}
                                                                  >
                                                                      {topic.label}
                                                                  </button>
                                                              );
                                                          })}
                                                      </div>
                                                  </div>
                                              )}
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {/* ORG-DEFINED SECTIONS: the client's own content areas. Each topic is a
                normal topic page; its resources are loaded in Content Library with the
                scope set to this organization. Saved with the button at the top. */}
            <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-sm font-bold text-slate-900">Custom sections</h3>
                <p className="text-xs text-slate-500 mb-4">This organization's own content areas, shown in the sidebar under their own heading. Add a section, then its topics. Load videos, PDFs and links for each topic in Content Library, with the scope set to this organization.</p>

                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Sidebar group heading</label>
                <input
                    className="w-full p-2 border rounded-lg text-sm mb-4"
                    placeholder="Defaults to the organization's name"
                    value={customNavLabel}
                    onChange={e => setCustomNavLabel(e.target.value)}
                />

                <div className="space-y-3">
                    {customNav.map((section, sIdx) => (
                        <div key={section.id || `new-${sIdx}`} className="p-3 rounded-xl border border-slate-200 bg-slate-50">
                            <div className="flex items-center gap-2">
                                <select
                                    className="p-2 border rounded-lg text-xs bg-white shrink-0"
                                    value={section.icon || CUSTOM_NAV_ICONS[0]}
                                    onChange={e => setCustomNav(customNav.map((s, i) => i === sIdx ? { ...s, icon: e.target.value } : s))}
                                    aria-label="Section icon"
                                >
                                    {CUSTOM_NAV_ICONS.map(name => <option key={name} value={name}>{name}</option>)}
                                </select>
                                <input
                                    className="flex-1 p-2 border rounded-lg text-sm font-bold bg-white"
                                    placeholder="Section heading, e.g. Hockey Culture"
                                    value={section.label}
                                    onChange={e => setCustomNav(customNav.map((s, i) => i === sIdx ? { ...s, label: e.target.value } : s))}
                                />
                                <button
                                    type="button"
                                    onClick={() => { if (window.confirm('Remove this section from the sidebar? Its loaded content is kept, but members can no longer reach it.')) setCustomNav(customNav.filter((_, i) => i !== sIdx)); }}
                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                                    title="Remove section"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            {section.id && <p className="text-[10px] text-slate-400 font-mono mt-1 pl-1">{section.id}</p>}

                            <div className="flex items-center gap-2 mt-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase shrink-0">Page type</label>
                                <select
                                    className="p-1.5 border rounded-lg text-xs bg-white"
                                    value={section.kind || 'section'}
                                    onChange={e => setCustomNav(customNav.map((s, i) => i === sIdx ? { ...s, kind: e.target.value, page: s.page || { ...EMPTY_PAGE } } : s))}
                                >
                                    <option value="section">Section with topics underneath</option>
                                    <option value="landing">Landing page (this entry is the page)</option>
                                </select>
                            </div>

                            {section.kind === 'landing' && (() => {
                                const pg = section.page || EMPTY_PAGE;
                                const setPage = (patch) => setCustomNav(customNav.map((s, i) => i === sIdx ? { ...s, page: { ...(s.page || EMPTY_PAGE), ...patch } } : s));
                                const field = 'w-full p-2 border rounded-lg text-xs bg-white';
                                const area = `${field} font-mono resize-none`;
                                return (
                                    <div className="mt-3 pl-4 border-l-2 border-slate-200 space-y-2">
                                        <div className="grid grid-cols-2 gap-2">
                                            <input className={field} placeholder="Eyebrow, e.g. Hockey Alberta" value={pg.eyebrow} onChange={e => setPage({ eyebrow: e.target.value })} />
                                            <input className={field} placeholder="Tagline, e.g. #ItStartsWithUs" value={pg.tagline} onChange={e => setPage({ tagline: e.target.value })} />
                                        </div>
                                        <input className={`${field} font-bold`} placeholder="Headline (defaults to the entry name)" value={pg.headline} onChange={e => setPage({ headline: e.target.value })} />
                                        <textarea rows={2} className={`${field} resize-none`} placeholder="Intro paragraph under the headline" value={pg.intro} onChange={e => setPage({ intro: e.target.value })} />
                                        <textarea rows={2} className={`${field} resize-none`} placeholder="Statement, e.g. the opening line of a pledge" value={pg.statement} onChange={e => setPage({ statement: e.target.value })} />
                                        <textarea rows={4} className={area} placeholder={'Commitments, one per line:  KEYWORD | text\nAWARE | Be aware of the language we use.'} value={pg.commitments} onChange={e => setPage({ commitments: e.target.value })} />
                                        <input className={field} placeholder="Steps heading (defaults to: How to take part)" value={pg.stepsTitle} onChange={e => setPage({ stepsTitle: e.target.value })} />
                                        <textarea rows={4} className={area} placeholder={'Steps, one per line:  Title | text'} value={pg.steps} onChange={e => setPage({ steps: e.target.value })} />
                                        <textarea rows={3} className={area} placeholder={'Buttons, one per line:  Label | tab id or https link\nThe first one is the main button.'} value={pg.actions} onChange={e => setPage({ actions: e.target.value })} />
                                        <p className="text-[10px] text-slate-400">A button can open another page here by its id (shown in grey under each entry, e.g. custom-respect-and-belonging) or link out with a full https address. Videos and PDFs for this page are added in Content Library.</p>
                                    </div>
                                );
                            })()}

                            {section.kind !== 'landing' && (
                            <div className="mt-3 pl-4 border-l-2 border-slate-200 space-y-2">
                                {section.subItems.map((topic, tIdx) => (
                                    <div key={topic.id || `new-${tIdx}`} className="bg-white border border-slate-200 rounded-lg p-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                className="flex-1 p-1.5 border rounded text-sm"
                                                placeholder="Topic, e.g. Respect & Belonging"
                                                value={topic.label}
                                                onChange={e => setCustomNav(customNav.map((s, i) => i !== sIdx ? s : { ...s, subItems: s.subItems.map((t, j) => j === tIdx ? { ...t, label: e.target.value } : t) }))}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setCustomNav(customNav.map((s, i) => i !== sIdx ? s : { ...s, subItems: s.subItems.filter((_, j) => j !== tIdx) }))}
                                                className="p-1.5 text-slate-400 hover:text-red-500 rounded shrink-0"
                                                title="Remove topic"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                        <input
                                            className="w-full p-1.5 border rounded text-xs mt-1.5"
                                            placeholder="One-line description shown at the top of the topic page"
                                            value={topic.description}
                                            onChange={e => setCustomNav(customNav.map((s, i) => i !== sIdx ? s : { ...s, subItems: s.subItems.map((t, j) => j === tIdx ? { ...t, description: e.target.value } : t) }))}
                                        />
                                        {topic.id && <p className="text-[10px] text-slate-400 font-mono mt-1">{topic.id}</p>}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setCustomNav(customNav.map((s, i) => i !== sIdx ? s : { ...s, subItems: [...s.subItems, { label: '', description: '' }] }))}
                                    className="text-xs font-bold text-teal-700 hover:text-teal-900 flex items-center gap-1"
                                >
                                    <Plus size={14} /> Add topic
                                </button>
                            </div>
                            )}
                        </div>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={() => setCustomNav([...customNav, { label: '', icon: CUSTOM_NAV_ICONS[0], kind: 'section', description: '', page: { ...EMPTY_PAGE }, subItems: [{ label: '', description: '' }] }])}
                    className="mt-3 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-xs font-bold text-slate-600 hover:border-slate-400 hover:bg-slate-50 flex items-center gap-1.5"
                >
                    <Plus size={14} /> Add section
                </button>
                <p className="text-[10px] text-slate-400 mt-3">Ids are created on first save and never change, so renaming a section keeps its link and its content. A section with no topics is not saved.</p>
            </div>
        </div>
    );
};

export default AdminNavBuilder;